/** "+ Add stage" ghost column at the end of the board (admin only). */
import { useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import type { StageAdmin } from '@/features/stages/use-stage-admin';

export function AddStageColumn({ admin, afterStageId }: { admin: StageAdmin; afterStageId?: string }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const openButtonRef = useRef<HTMLButtonElement>(null);
  // Closing the form unmounts the focused input; return focus to the ghost
  // button once it is back in the DOM (WCAG 2.4.3).
  const close = () => {
    setEditing(false);
    requestAnimationFrame(() => openButtonRef.current?.focus());
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    admin.add(name, 'open', afterStageId, () => {
      setName('');
      close();
    });
  };

  if (!editing) {
    return (
      <div className="w-[272px] shrink-0 pt-0.5">
        <button
          type="button"
          ref={openButtonRef}
          onClick={() => setEditing(true)}
          className="flex min-h-10 w-full items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 text-[13px] font-medium text-muted hover:border-navy/50 hover:bg-white/60 hover:text-navy"
        >
          <Plus size={15} strokeWidth={2.2} aria-hidden /> Add stage
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="w-[272px] shrink-0 rounded-lg border border-gray-200 bg-white p-2.5 shadow-sm">
      <label className="block text-[12px] font-medium text-muted" htmlFor="new-stage-name">
        New stage name
      </label>
      <input
        id="new-stage-name"
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && close()}
        placeholder="e.g. Reference check"
        className="mt-1 block min-h-10 w-full rounded-md border border-gray-300 px-2.5 text-sm"
      />
      <div className="mt-2 flex gap-2">
        <button
          type="submit"
          disabled={!name.trim() || admin.adding}
          className="inline-flex min-h-10 items-center rounded-md bg-navy px-3 text-[13px] font-medium text-white hover:bg-navy-dark disabled:opacity-50"
        >
          {admin.adding ? 'Adding…' : 'Add stage'}
        </button>
        <button
          type="button"
          onClick={close}
          className="inline-flex min-h-10 items-center rounded-md px-3 text-[13px] font-medium text-gray-700 hover:bg-page"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
